import React, { useState } from "react";
import { toast } from "react-toastify";
import { SettingsAPI, StaffMember } from "../../../services/settings.service";
import { ApiError } from "../../../services/api.errors";
import { avatarColor, initials } from "./accessHelpers";
import { AssignRolesModal } from "./roles/AssignRolesModal";
import { Role } from "../../../services/roles.service";

interface Props {
    isOpen:          boolean;
    onClose:         () => void;
    staff:           StaffMember[];
    onAddMember:     () => void;
    onMemberUpdate?: (updated: StaffMember) => void;
}

/** Full team-access table shown as a modal (opened from "Grant New Access"). */
export const AccessControlModal: React.FC<Props> = ({
    isOpen,
    onClose,
    staff,
    onAddMember,
    onMemberUpdate,
}) => {
    const [acting, setActing] = useState<string | null>(null);
    const [confirmingRevoke, setConfirmingRevoke] = useState<string | null>(null);
    const [manageRolesFor, setManageRolesFor] = useState<StaffMember | null>(null);

    if (!isOpen) return null;

    const handleRolesSaved = (userId: string, roles: Role[]) => {
        const member = staff.find(m => m.user_id === userId);
        if (member) {
            onMemberUpdate?.({
                ...member,
                roles: roles.map(r => ({ role_id: r.role_id, name: r.name })),
            });
        }
    };

    const handleRevoke = async (member: StaffMember) => {
        if (confirmingRevoke !== member.user_id) {
            setConfirmingRevoke(member.user_id);
            return;
        }
        setConfirmingRevoke(null);
        setActing(member.user_id);
        try {
            const updated = await SettingsAPI.revokeStaff(member.user_id);
            onMemberUpdate?.(updated);
            toast.success(`Access revoked for ${member.first_name} ${member.last_name}.`);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to revoke access.");
        } finally {
            setActing(null);
        }
    };

    const handleReactivate = async (member: StaffMember) => {
        setActing(member.user_id);
        try {
            const updated = await SettingsAPI.reactivateStaff(member.user_id);
            onMemberUpdate?.(updated);
            toast.success(`${member.first_name} ${member.last_name} has been reactivated.`);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to reactivate.");
        } finally {
            setActing(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 p-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white flex-shrink-0">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-base font-semibold text-gray-900">Access Control &amp; Permissions</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Manage who can access patient records and system features</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            onClick={onAddMember}
                            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                            </svg>
                            Add Member
                        </button>
                        <button onClick={onClose} title="Close"
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Body — scrollable member list */}
                <div className="overflow-y-auto px-3 py-2">
                    {staff.length === 0 ? (
                        <p className="text-sm text-gray-400 py-8 text-center">No staff members found.</p>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {staff.map((m, i) => {
                                const name = `${m.first_name} ${m.last_name}`.trim() || m.email;
                                const isActing = acting === m.user_id;
                                // Admins hold the clinic's access control; the backend
                                // refuses to revoke one (and the clinic owner), so the
                                // action is not offered.
                                const isRevocable = m.role !== "Admin";
                                return (
                                    <div key={m.user_id} className="flex items-center gap-4 px-3 py-3">
                                        {/* Avatar + name */}
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatarColor(i)} flex items-center justify-center text-white text-sm font-semibold flex-shrink-0`}>
                                                {initials(m.first_name, m.last_name, m.email)}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                                                <p className="text-xs text-gray-400 truncate">{m.email}</p>
                                            </div>
                                        </div>
                                        {/* Role / designation */}
                                        <div className="hidden sm:block w-40 min-w-0">
                                            <p className="text-sm text-gray-700 truncate">{m.designation || m.role}</p>
                                            <p className="text-xs text-gray-400 truncate">{m.role}</p>
                                        </div>
                                        {/* RBAC role chips */}
                                        <div className="hidden md:flex flex-wrap gap-1 w-36 flex-shrink-0">
                                            {m.roles.length === 0 ? (
                                                <span className="text-xs text-gray-300">No RBAC roles</span>
                                            ) : m.roles.slice(0, 2).map(r => (
                                                <span key={r.role_id} className="text-[10px] font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded-full">
                                                    {r.name}
                                                </span>
                                            ))}
                                            {m.roles.length > 2 && (
                                                <span className="text-[10px] text-gray-400">+{m.roles.length - 2}</span>
                                            )}
                                        </div>
                                        {/* Status badge */}
                                        <div className="hidden sm:flex items-center gap-1.5 w-20 flex-shrink-0">
                                            <span className={`w-2 h-2 rounded-full ${m.is_active ? "bg-green-500" : "bg-gray-300"}`} />
                                            <span className={`text-xs ${m.is_active ? "text-green-600" : "text-gray-400"}`}>
                                                {m.is_active ? "Active" : "Inactive"}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setManageRolesFor(m)}
                                            title="Manage roles"
                                            className="px-2.5 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors flex-shrink-0"
                                        >
                                            Manage roles
                                        </button>
                                        {/* Revoke / Reactivate */}
                                        {m.is_active && !isRevocable ? null : m.is_active ? (
                                            confirmingRevoke === m.user_id ? (
                                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                                    <button
                                                        type="button"
                                                        disabled={isActing}
                                                        onClick={() => handleRevoke(m)}
                                                        className="px-2.5 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40 transition-colors"
                                                    >
                                                        {isActing ? "…" : "Confirm"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setConfirmingRevoke(null)}
                                                        className="px-2.5 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                            <button
                                                type="button"
                                                disabled={isActing}
                                                onClick={() => handleRevoke(m)}
                                                title="Revoke access"
                                                className="px-2.5 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                                            >
                                                Revoke
                                            </button>
                                            )
                                        ) : (
                                            <button
                                                type="button"
                                                disabled={isActing}
                                                onClick={() => handleReactivate(m)}
                                                title="Reactivate"
                                                className="px-2.5 py-1.5 text-xs font-medium text-green-600 border border-green-200 rounded-lg hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                                            >
                                                {isActing ? "…" : "Reactivate"}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 flex justify-between items-center">
                    <span className="text-xs text-gray-400">
                        {staff.length} member{staff.length === 1 ? "" : "s"}
                    </span>
                    <button onClick={onClose}
                        className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        Close
                    </button>
                </div>
            </div>

            <AssignRolesModal
                isOpen={manageRolesFor !== null}
                member={manageRolesFor}
                onClose={() => setManageRolesFor(null)}
                onSaved={handleRolesSaved}
            />
        </div>
    );
};
